import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, Plus, Edit, Trash2, Eye, Lock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import DeleteProductDialog from "@/components/products/DeleteProductDialog";
import {
  CompactStatCard,
  DataTable,
  PageHeader,
  StandardImage,
  StatsGrid,
  useDataTable,
  type DataTableColumn,
  type DataTableFilterConfig,
} from "@/components/shared";
import { useDeleteStoreProductMutation, useMyStore, useStoreCatalog, useStoreId } from "@/hooks/queries";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";
import type { StoreProductRow } from "@/types/store.ui";

const statusBadge: Record<string, string> = {
  active: "bg-success/10 text-success border-success/20",
  pending: "bg-warning/10 text-warning border-warning/20",
  rejected: "bg-destructive/10 text-destructive border-destructive/20",
  needs_revision: "bg-orange-500/10 text-orange-600 border-orange-500/20",
  out_of_stock: "bg-muted text-muted-foreground border-border",
};

const statusFilters: DataTableFilterConfig[] = [
  {
    id: "status",
    placeholder: "Status",
    options: [
      { label: "All", value: "all" },
      { label: "Active", value: "active" },
      { label: "Pending", value: "pending" },
      { label: "Rejected", value: "rejected" },
      { label: "Needs revision", value: "needs_revision" },
      { label: "Out of Stock", value: "out_of_stock" },
    ],
  },
];

function formatStock(stock: number | null, status: StoreProductRow["status"]) {
  if (stock !== null) return String(stock);
  return status === "out_of_stock" ? "Out" : "In stock";
}

const StoreProducts = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { storeId, isLoading: storeLoading } = useStoreId();
  const { data: store } = useMyStore(Boolean(storeId));
  const { data, isLoading: productsLoading } = useStoreCatalog(storeId);
  const deleteMutation = useDeleteStoreProductMutation(storeId ?? 0);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const availableBv = store?.availableBv ?? 0;

  const storeProducts = data?.items ?? [];

  const table = useDataTable<StoreProductRow>({
    data: storeProducts,
    searchKeys: ["name", "category"],
    filterConfigs: statusFilters,
    filterPredicate: (row, state) =>
      state.filters.status === "all" || row.status === state.filters.status,
  });

  const stats = useMemo(
    () => ({
      total: storeProducts.length,
      active: storeProducts.filter((p) => p.status === "active").length,
      pending: storeProducts.filter((p) => p.status === "pending").length,
      outOfStock: storeProducts.filter((p) => p.status === "out_of_stock").length,
    }),
    [storeProducts],
  );

  const columns: DataTableColumn<StoreProductRow>[] = [
    {
      id: "name",
      header: "Product",
      accessorKey: "name",
      sortable: true,
      mobilePriority: 1,
      cell: (product) => (
        <div className="flex items-center gap-3">
          <StandardImage src={product.image} alt={product.name} standard="product" className="h-10 w-10" />
          <div className="space-y-0.5">
            <Link to={`/store/products/${product.id}`} className="font-semibold hover:text-primary hover:underline line-clamp-1">
              {product.name}
            </Link>
            {product.isAdminProduct && (
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 text-[10px] py-0 px-1 font-medium">
                Admin Supplied
              </Badge>
            )}
          </div>
        </div>
      ),
    },
    {
      id: "category",
      header: "Category",
      accessorKey: "category",
      sortable: true,
      mobileLabel: "Category",
      mobilePriority: 2,
      className: "text-sm",
    },
    {
      id: "price",
      header: "Price",
      accessorKey: "price",
      sortable: true,
      align: "right",
      mobileLabel: "Price",
      mobilePriority: 3,
      cell: (product) => <span className="font-medium">₹{product.price}</span>,
    },
    {
      id: "stock",
      header: "Stock",
      accessorKey: "stock",
      sortable: true,
      align: "right",
      mobileLabel: "Stock",
      mobilePriority: 4,
      cell: (product) => formatStock(product.stock, product.status),
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "status",
      sortable: true,
      mobileLabel: "Status",
      mobilePriority: 5,
      cell: (product) => (
        <Badge variant="outline" className={statusBadge[product.status]}>
          {product.status.replace("_", " ")}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      align: "center",
      hideOnMobile: true,
      cell: (product) => (
        <div className="flex items-center justify-center gap-1">
          <Button size="icon" variant="ghost" className="h-8 w-8" asChild>
            <Link to={`/store/products/${product.id}`} title="View Product"><Eye className="h-4 w-4" /></Link>
          </Button>
          {product.isAdminProduct ? (
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-amber-600 hover:text-amber-700 hover:bg-amber-500/10"
              title="Admin-created product is locked. To increase stock, purchase more from Store Supply."
              asChild
            >
              <Link to="/store/supply">
                <Lock className="h-4 w-4" />
              </Link>
            </Button>
          ) : (
            <>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => navigate(`/store/products/${product.id}/edit`)} title="Edit Product">
                <Edit className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => setDeleteTarget({ id: product.id, name: product.name })} title="Delete Product">
                <Trash2 className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  const handleDelete = async () => {
    if (!deleteTarget || !storeId) return;
    try {
      await deleteMutation.mutateAsync(Number(deleteTarget.id));
      toast({ title: "Product deleted" });
      setDeleteTarget(null);
    } catch (error) {
      toast({
        title: "Failed to delete product",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  if (storeLoading || productsLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid gap-4 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  if (!storeId) {
    return (
      <PageHeader title="Products" description="No store linked to your account." />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Products"
        description="Manage your product catalog."
        actions={
          <Button
            className="gap-2"
            disabled={availableBv <= 0}
            onClick={() => navigate("/store/products/new")}
            title={availableBv <= 0 ? "Receive BV from admin before adding products" : undefined}
          >
            <Plus className="h-4 w-4" /> Add Product
          </Button>
        }
      />

      {availableBv <= 0 ? (
        <p className="rounded-lg border border-warning/30 bg-warning/5 px-4 py-3 text-sm">
          Store BV is zero.{" "}
          <Link to="/store/bv" className="font-medium text-primary underline">
            View Receive BV
          </Link>{" "}
          — ask admin to credit BV before adding products.
        </p>
      ) : null}

      <StatsGrid>
        <CompactStatCard
          title="Available BV"
          value={Number.isInteger(availableBv) ? availableBv : Number(availableBv).toFixed(2)}
          icon={Package}
        />
        <CompactStatCard title="Total Products" value={stats.total} icon={Package} />
        <CompactStatCard title="Active" value={stats.active} icon={Package} />
        <CompactStatCard title="Pending" value={stats.pending} icon={Package} />
      </StatsGrid>

      <DataTable
        title="All Products"
        columns={columns}
        data={table.rows}
        getRowId={(row) => row.id}
        search={table.state.search}
        onSearchChange={table.setSearch}
        searchPlaceholder="Search products..."
        filters={statusFilters}
        filterValues={table.state.filters}
        onFilterChange={table.setFilter}
        sort={table.state.sort}
        onSortChange={table.toggleSort}
        pagination={table.pagination}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        renderMobileCard={(product) => (
          <div className="flex items-start gap-3 rounded-lg border border-border p-3">
              <StandardImage src={product.image} alt={product.name} standard="product" className="h-14 w-14 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Link to={`/store/products/${product.id}`} className="font-semibold text-sm hover:text-primary hover:underline">
                      {product.name}
                    </Link>
                    {product.isAdminProduct && (
                      <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 text-[10px] py-0 px-1 font-medium">
                        Admin Supplied
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{product.category} • Stock: {formatStock(product.stock, product.status)}</p>
                </div>
                <Badge variant="outline" className={statusBadge[product.status]}>{product.status.replace("_", " ")}</Badge>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="font-bold text-sm">₹{product.price}</span>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-8 w-8" asChild>
                    <Link to={`/store/products/${product.id}`} title="View Product"><Eye className="h-4 w-4" /></Link>
                  </Button>
                  {product.isAdminProduct ? (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-amber-600 hover:text-amber-700 hover:bg-amber-500/10"
                      title="Admin-created product is locked. To increase stock, purchase more from Store Supply."
                      asChild
                    >
                      <Link to="/store/supply">
                        <Lock className="h-4 w-4" />
                      </Link>
                    </Button>
                  ) : (
                    <>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => navigate(`/store/products/${product.id}/edit`)} title="Edit Product">
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => setDeleteTarget({ id: product.id, name: product.name })} title="Delete Product">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      />

      <DeleteProductDialog
        open={Boolean(deleteTarget)}
        productName={deleteTarget?.name ?? ""}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default StoreProducts;
